import React, { useState, useEffect } from 'react'
import { Modal, Typography, ColorCircularProgress, Button } from 'components'
import { makeStyles } from '@material-ui/core/styles'
import { colors } from 'constants/index'
import { useDispatch } from 'react-redux'
import { getCasesWithPro } from '../../store/actions/connections'
import { openVaultModal, setVaultOpenPlace, setVaultCase, setVaultSelectedFolders } from '../../store/actions/vault'

const { WHITE } = colors

const useStyles = makeStyles({
  modal: {
    minWidth: 440,
    position: 'relative',
    padding: '24px',
    backgroundColor: WHITE,
    boxShadow: '2px 4px 24px 0px rgba(0, 0, 0, 0.15)',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: 20,
    fontWeight: 500,
    lineHeight: '28px',
    color: '#011A33',
  },
  description: {
    margin: '0 0 16px 0',
    fontSize: 15,
    lineHeight: '22px',
    color: '#6F8194',
    fontWeight: 400,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  projectItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '4px 8px',
    borderRadius: 4,
    '&:hover': {
      backgroundColor: '#FEF9F6',
    },
  },
  projectItemSelected: {
    backgroundColor: '#FEF9F6',
  },
  projectName: {
    flex: 1,
    fontSize: 15,
    lineHeight: '22px',
    color: '#011A33',
    fontWeight: 400,
    '&.selected': {
      color: '#E55107',
    },
  },
  goToVaultButton: {
    fontSize: 11,
    fontWeight: 500,
    lineHeight: '12px',
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    color: '#EE5F10',
    padding: '10px 0',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
})

const SelectProjectModal = ({ isOpen, onClose, proUuid, proName, vaultPlace = 'connections' }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [projects, setProjects] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen && proUuid) {
      setIsLoading(true)
      dispatch(getCasesWithPro(proUuid))
        .then(data => {
          setProjects(Array.isArray(data) ? data : [])
          setIsLoading(false)
        })
        .catch(() => {
          setIsLoading(false)
        })
    } else if (!isOpen) {
      setProjects([])
    }
  }, [isOpen, proUuid, dispatch])

  const handleGoToVault = project => {
    dispatch(setVaultOpenPlace(vaultPlace))
    // Reset selectedFolders to ensure vault navigates directly to the case folder
    dispatch(setVaultSelectedFolders([]))
    dispatch(
      openVaultModal({ proID: project.pro_uuid, proName: proName || project.pro_full_name, caseID: project.uuid })
    )
    onClose()
  }

  return (
    <Modal open={isOpen} handleClose={onClose} maxWidth={false}>
      {isLoading ? (
        <ColorCircularProgress />
      ) : (
        <div className={classes.modal}>
          <Typography variant="h3" className={classes.title}>
            Select a project
          </Typography>
          <Typography className={classes.description}>Select a project you want to open the vault for</Typography>
          <div className={classes.list}>
            {projects.length === 0 ? (
              <Typography style={{ color: '#6F8194', fontSize: 15 }}>No projects available</Typography>
            ) : (
              projects.map((project, index) => (
                <div key={project.uuid || index} className={classes.projectItem}>
                  <Typography className={classes.projectName}>{project.name}</Typography>
                  <Button variant="text" className={classes.goToVaultButton} onClick={() => handleGoToVault(project)}>
                    Go to vault
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}

export default SelectProjectModal
